type RgbReturn = {
    values: HSLArray;
    string: string;
};

export const rgbResult = ([r, g, b, a]: HSLArray): RgbReturn => {
    let result: RgbReturn = {
        values: [r, g, b],
        string: `rgb(${r}, ${g}, ${b})`,
    };

    if (a !== undefined && a !== 100) {
        result.values[3] = a;
        result.string = result.string.replace(/\)$/, ', ' + a + '%)');
    }

    return result;
};

export const hslToRgb = ([h, s, l]: HSLArray, alpha?: number): RgbReturn => {
    s /= 100;
    l /= 100;

    let c = (1 - Math.abs(2 * l - 1)) * s,
        x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
        m = l - c / 2,
        r = 0,
        g = 0,
        b = 0;

    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    } else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    } else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    } else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    } else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    } else if (300 <= h && h < 360) {
        r = c;
        g = 0;
        b = x;
    }

    r = ~~((r + m) * 255 + 0.5);
    g = ~~((g + m) * 255 + 0.5);
    b = ~~((b + m) * 255 + 0.5);

    return rgbResult([r, g, b, alpha]);
};

type HslReturn = {
    values: HSLArray;
    string: string;
};

export const hslResult = ([h, s, l, a]: HSLArray): HslReturn => {
    let result: HslReturn = {
        values: [h, s, l],
        string: `hsl(${h}, ${s}%, ${l}%)`,
    };

    if (a !== undefined && a !== 100) {
        result.values[3] = a;
        result.string = result.string.replace(/\)$/, ', ' + a + '%)');
    }

    return result;
};
export const rgbToHsl = ([r, g, b]: RGBArray, alpha?: number): HslReturn => {
    r /= 255;
    g /= 255;
    b /= 255;

    let max = Math.max(r, g, b),
        min = Math.min(r, g, b),
        h = 0,
        s = 0,
        l = (max + min) / 2;

    if (max !== min) {
        let d = max - min;
        s = l > 0.5 ? d / (2 - d) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }

        h /= 6;
    }

    h = ~~(h * 360 + 0.5);
    s = ~~(s * 100 + 0.5);
    l = ~~(l * 100 + 0.5);

    return hslResult([h, s, l, alpha]);
};

type HexReturn = {
    values: HEXArray;
    string: string;
};
export const rgbToHex = ([r, g, b]: RGBArray, alpha?: number): HexReturn => {
    let hex = {
        r: r ? r.toString(16) : '00',
        g: g ? g.toString(16) : '00',
        b: b ? b.toString(16) : '00',
    };

    let result: HexReturn = {
        values: [hex.r, hex.g, hex.b],
        string: `#${hex.r}${hex.g}${hex.b}`,
    };

    if (alpha !== undefined && alpha !== 1) {
        let alphaHex = (~~(alpha * 255)).toString(16);

        result.values[3] = alphaHex;
        result.string += alphaHex;
    }

    return result;
};

export const hslToHex = (input: HSLArray, alpha?: number): HexReturn => rgbToHex(hslToRgb(input, alpha).values, alpha);

export type ColorFormatResult<T = HSLArray | RGBArray | HEXArray> = {
    values: T;
    string: string;
};
type ColorFormatReturn =
    | {
          [key in 'hsl' | 'rgb' | 'hex']?: ColorFormatResult;
      }
    | ColorFormatResult;
export const formatColor = (input: HSLArray | string, format: 'hsl' | 'rgb' | 'hex' | ('hsl' | 'rgb' | 'hex')[] = 'hsl'): ColorFormatReturn => {
    let rgb: ColorFormatResult = {
            values: [0, 0, 0],
            string: 'rgb(0,0,0)',
        },
        hsl: ColorFormatResult = {
            values: [0, 0, 0],
            string: 'hsl(0,0,0)',
        },
        hex: ColorFormatResult = {
            values: ['0', '0', '0'],
            string: '#000',
        },
        alpha = 1;

    if (typeof input === 'object') hsl = hslResult(input);
    else if (typeof input === 'string') {
        if (/^#/.test(input)) {
            let match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(input);

            if (match) {
                let clr: RGBArray = [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)];

                if (match[4]) alpha = ~~(parseInt(match[4], 16) / 2.55 + 0.5) / 100;

                console.log(clr);

                hsl = rgbToHsl(clr, alpha);
                rgb = rgbResult(clr.concat(alpha * 100) as RGBArray);
            }
        } else if (/^rgb/i.test(input)) {
            let clr = <RGBArray>input
                .replace(/rgb.?\(|\)/g, '')
                .trim()
                .replace(/,\s?|\s?\/\s?|\s/g, '.')
                .split('.')
                .map((entry: string) => parseInt(entry));

            if (clr[3]) {
                alpha = clr[3];

                if (alpha > 1) alpha /= 100;

                clr.pop();
            }

            hsl = rgbToHsl(clr, alpha);
            rgb = rgbResult(clr.concat(alpha * 100) as RGBArray);
        } else if (/^hsl/i.test(input)) {
            let clr = <HSLArray>input
                .replace(/hsl.?\(|\)/g, '')
                .trim()
                .replace(/,\s?|\s?\/\s?|\s/g, '.')
                .split('.')
                .map((entry: string) => parseInt(entry));

            if (clr[3]) {
                alpha = clr[3];

                if (alpha > 1) alpha /= 100;

                clr.pop();
            }

            hsl = hslResult(clr.concat(alpha * 100) as HSLArray);
        }
    }

    let result: ColorFormatReturn = {};

    if (format === 'hsl') return hsl;
    if (format === 'rgb') return rgb.string === 'rgb(0,0,0)' ? hslToRgb(<HSLArray>hsl.values, alpha) : rgb;
    if (format === 'hex') return hex.string === '#000' ? hslToHex(<HSLArray>hsl.values, alpha) : hex;

    if (typeof format === 'object') {
        result = {};

        for (let i = 0; i < format.length; i++) {
            if (format[i] === 'hsl') result.hsl = hsl;

            if (format[i] === 'rgb') result.rgb = rgb.string === 'rgb(0,0,0)' ? hslToRgb(<HSLArray>hsl.values, alpha) : rgb;

            if (format[i] === 'hex')
                result.hex =
                    hex.string === '#000' ? (rgb.string === 'rgb(0,0,0)' ? hslToHex(<HSLArray>hsl.values, alpha) : rgbToHex(<RGBArray>rgb.values, alpha)) : hex;
        }
    }

    return result;
};
